import {
  Navigate,
  RouterProvider,
  createBrowserRouter
} from "react-router-dom";
import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import PageWrapper from "../components/PageWrapper";
import getCorePersonalStore from "../stores/core/core-personal-store";
import ApproveReject from "./approve-reject";
import Audit from "./auditor/audit";
import AuditedEvents from "./auditor/audited-events";
import CSRSPerformance from "./auditor/csrs-performance";
import EventsChart from "./auditor/events-chart";
import MyAuditingMistakes from "./auditor/my-auditing-mistakes";
import MyAuditingPerformance from "./auditor/my-auditing-performance";
import AdminPool from "./auditor/pool";
import PromoCode from "./auditor/promo-code";
import UserRateUs from "./auditor/user-rate-us";
import WorkloadData from "./auditor/workload-data";
import AccountsDeletion from "./communications/accounts-deletion";
import PromoCodeGenerator from "./communications/promo-code-generator";
import ChangePassword from "./csr/change-password";
import MyCSRMistakes from "./csr/my-csr-mistakes";
import MyCSRPerformance from "./csr/my-csr-performance";
import MyCSRPrevActions from "./csr/my-csr-prev-actions";
import MyCSRReceiptEventCount from "./csr/my-csr-receipt-event-count";
import BlockedRequests from "./developer/blocked-requests";
import FlaggedConnectionsDashboard from "./developer/flagged-connections-dashboard";
import FlushHosts from "./developer/flush-hosts";
import OnCallTeam from "./developer/on-call-team";
import ReassignKhataba from "./developer/ReassignKhataba";
import StatsDashboard from "./developer/stats-dashboard";
import TurnOffAlarms from "./developer/turn-off-alarms";
import WatchAlerts from "./developer/watch-alerts";
import InvestigationCases from "./investigation-cases";
import KhatabatClients from "./khattabat/KhatabatClients";
import KhattabatSearchingAnd101M from "./khattabat/matching/search-and-101m";
import Login from "./login";
import ChangeEmailSubscription from "./qa/change-email-subscription";
import ChangeStatus from "./qa/change-status";
import ObtainIosReceiptTokens from "./qa/obtain-receipt-token";
import Reports from "./reports";
import GiveSubscriptions from "./scsr/give-subscriptions";
import ShareholdersDashboard from "./shareholder";
import FlaggedDescriptions from "./supervisor/flagged-descriptions";
import KhattabatRemuneration from "./khattabat/khattabat-remuneration";
import AdminPermission from "./supervisor/permission";
import ResetPasswords from "./supervisor/reset-passwords";
import Supervise from "./supervisor/supervise";
import SupervisedEvents from "./supervisor/supervised-events";
import SupervisorReceiptCompensation from "./supervisor/supervisor-receipt-compensation";
import SupervisorReceiptConfiguration from "./supervisor/supervisor-receipt-configuration";
import ConversationsPage from "./user/conversations";
import UserAdvanced from "./user/user-advanced";
import UserDevices from "./user/user-devices";
import SilentlyBannedUsers from "./user/silently-banned-users";
import UserEditProfile from "./user/user-edit-profile";
import UserEvents from "./user/user-events";
import UserLogin from "./user/user-login";
import UserMessagesId from "./user/user-message-id";
import UserMessages from "./user/user-messages";
import UserPayments from "./user/user-payments";
import UserPromoCodeUsage from "./user/user-promo-code-usage";
import UserReferralCodeUsage from "./user/user-referral-code-usage";
import Alarms from "./alarms";
import KhattabatResults from "./khattabat/matching/results";
import AuditorsEventsData from "./auditor/auditors-events";
import TurnOnOffKhattabat from "./khattabat/turn-on-off-khattabat";
import KhattabatData from "./khattabat/khattabat-data";
import KhattabatNewClients from "./khattabat/new-clients";
import DebugImageAI from "./developer/debug-image-ai";
import UserSelfieVerificationPhotos from "./user/user-selfie-verification-photos";
import UserAIDescriptions from "./user/user-ai-descriptions";
import KhattabatFunnelStats from "./khattabat/khattabat-funnel-stats";
import AdminUsage from "./developer/admin-usage";
import AIGeneratedDescriptionAll from "./developer/ai-generated-description-all";
import TransferUserPackage from "./developer/transfer-user-package";
import WebPayments from "./auditor/web-payments";
import OtherAuditorsPerformance from "./supervisor/other-auditors-performance";
import ReportActionAnalytics from "./auditor/report-actions-analytics";
import UserConsumables from "./user/user-consumables";
import KhattabatMatchmakingAudit from "./khattabat/khattabat-matchmaking-audit";

const RouterComponent = observer(() => {
  const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
  const router = useMemo(() => {
    const routes = [
      {
        path: "/",
        element: <PageWrapper isBlocker={false} Page={Login} />
      },
      !isAnyKhattabaAdmin && {
        path: "/alarms",
        element: <PageWrapper isBlocker={false} Page={Alarms} />
      },
      !isAnyKhattabaAdmin && {
        path: "/approveReject",
        element: <PageWrapper isBlocker={false} Page={ApproveReject} />
      },
      !isAnyKhattabaAdmin && {
        path: "/reports",
        element: <PageWrapper isBlocker={false} Page={Reports} />
      },
      !isAnyKhattabaAdmin && {
        path: "/investigationCases",
        element: <PageWrapper isBlocker={false} Page={InvestigationCases} />
      },
      {
        path: "/users/advanced",
        element: <PageWrapper isBlocker={false} Page={UserAdvanced} />
      },
      {
        path: "/users/consumables",
        element: <PageWrapper isBlocker={false} Page={UserConsumables} />
      },
      {
        path: "/users/conversations",
        element: <PageWrapper isBlocker={false} Page={ConversationsPage} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/devices",
        element: <PageWrapper isBlocker={false} Page={UserDevices} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/editProfile",
        element: <PageWrapper isBlocker={false} Page={UserEditProfile} />
      },
      {
        path: "/users/silentlyBanned",
        element: <PageWrapper isBlocker={false} Page={SilentlyBannedUsers} />
      },
      {
        path: "/users/events",
        element: <PageWrapper isBlocker={false} Page={UserEvents} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/login",
        element: <PageWrapper isBlocker={false} Page={UserLogin} />
      },
      {
        path: "/users/messages",
        element: <PageWrapper isBlocker={false} Page={UserMessages} />
      },
      {
        path: "/users/messageid",
        element: <PageWrapper isBlocker={false} Page={UserMessagesId} />
      },
      {
        path: "/users/payments",
        element: <PageWrapper isBlocker={false} Page={UserPayments} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/referralCodeUsage",
        element: <PageWrapper isBlocker={false} Page={UserReferralCodeUsage} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/promoCodeUsage",
        element: <PageWrapper isBlocker={false} Page={UserPromoCodeUsage} />
      },
      !isAnyKhattabaAdmin && {
        path: "/users/selfieVerificationPhotos",
        element: (
          <PageWrapper isBlocker={false} Page={UserSelfieVerificationPhotos} />
        )
      },
      {
        path: "/users/aiDescriptions",
        element: <PageWrapper isBlocker={false} Page={UserAIDescriptions} />
      },
      !isAnyKhattabaAdmin && {
        path: "/csr/my-csr-performance",
        element: <PageWrapper isBlocker={false} Page={MyCSRPerformance} />
      },
      !isAnyKhattabaAdmin && {
        path: "/csr/my-csr-mistakes",
        element: <PageWrapper isBlocker={false} Page={MyCSRMistakes} />
      },
      !isAnyKhattabaAdmin && {
        path: "/csr/my-csr-prev-actions",
        element: <PageWrapper isBlocker={false} Page={MyCSRPrevActions} />
      },
      {
        path: "/csr/changePassword",
        element: <PageWrapper isBlocker={false} Page={ChangePassword} />
      },
      !isAnyKhattabaAdmin && {
        path: "/csr/myCSRReceiptEventCount",
        element: <PageWrapper isBlocker={false} Page={MyCSRReceiptEventCount} />
      },
      {
        path: "/scsr/giveSubscriptions",
        element: <PageWrapper isBlocker={false} Page={GiveSubscriptions} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/audit",
        element: <PageWrapper isBlocker={false} Page={Audit} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/audited-events",
        element: <PageWrapper isBlocker={false} Page={AuditedEvents} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/eventsData",
        element: <PageWrapper isBlocker={false} Page={AuditorsEventsData} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/my-auditing-performance",
        element: <PageWrapper isBlocker={false} Page={MyAuditingPerformance} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/my-auditing-mistakes",
        element: <PageWrapper isBlocker={false} Page={MyAuditingMistakes} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/csrs-performance",
        element: <PageWrapper isBlocker={false} Page={CSRSPerformance} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/report-actions-analytics",
        element: <PageWrapper isBlocker={false} Page={ReportActionAnalytics} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/pool",
        element: <PageWrapper isBlocker={false} Page={AdminPool} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/workloadData",
        element: <PageWrapper isBlocker={false} Page={WorkloadData} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/eventCharts",
        element: <PageWrapper isBlocker={false} Page={EventsChart} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/promoCode",
        element: <PageWrapper isBlocker={false} Page={PromoCode} />
      },
      {
        path: "/auditor/webPayments",
        element: <PageWrapper isBlocker={false} Page={WebPayments} />
      },
      !isAnyKhattabaAdmin && {
        path: "/auditor/userRateUsData",
        element: <PageWrapper isBlocker={false} Page={UserRateUs} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/permission",
        element: <PageWrapper isBlocker={false} Page={AdminPermission} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/supervise",
        element: <PageWrapper isBlocker={false} Page={Supervise} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/supervised-events",
        element: <PageWrapper isBlocker={false} Page={SupervisedEvents} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/other-auditors-performance",
        element: (
          <PageWrapper isBlocker={false} Page={OtherAuditorsPerformance} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/resetPasswords",
        element: <PageWrapper isBlocker={false} Page={ResetPasswords} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/supervisorReceiptCompensation",
        element: (
          <PageWrapper isBlocker={false} Page={SupervisorReceiptCompensation} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/supervisorReceiptConfiguration",
        element: (
          <PageWrapper
            isBlocker={false}
            Page={SupervisorReceiptConfiguration}
          />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/remuneration",
        element: <PageWrapper isBlocker={false} Page={KhattabatRemuneration} />
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/data",
        element: <PageWrapper isBlocker={false} Page={KhattabatData} />
      },
      !isAnyKhattabaAdmin && {
        path: "/supervisor/flaggedDescription",
        element: <PageWrapper isBlocker={false} Page={FlaggedDescriptions} />
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/turnOnOff",
        element: <PageWrapper isBlocker={false} Page={TurnOnOffKhattabat} />
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/funnelStats",
        element: <PageWrapper isBlocker={false} Page={KhattabatFunnelStats} />
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/matchmakingAudit",
        element: (
          <PageWrapper isBlocker={false} Page={KhattabatMatchmakingAudit} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/communications/accountsDeletion",
        element: <PageWrapper isBlocker={false} Page={AccountsDeletion} />
      },
      !isAnyKhattabaAdmin && {
        path: "/communications/promoCodeGenerator",
        element: <PageWrapper isBlocker={false} Page={PromoCodeGenerator} />
      },
      !isAnyKhattabaAdmin && {
        path: "/QA/changeStatus",
        element: <PageWrapper isBlocker={false} Page={ChangeStatus} />
      },
      !isAnyKhattabaAdmin && {
        path: "/QA/changeEmailSubscription",
        element: (
          <PageWrapper isBlocker={false} Page={ChangeEmailSubscription} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/QA/obtainIosReceiptTokens",
        element: <PageWrapper isBlocker={false} Page={ObtainIosReceiptTokens} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/statsDashboard",
        element: <PageWrapper isBlocker={false} Page={StatsDashboard} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/onCallTeam",
        element: <PageWrapper isBlocker={false} Page={OnCallTeam} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/blockedRequests",
        element: <PageWrapper isBlocker={false} Page={BlockedRequests} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/flaggedConnectionsDashboard",
        element: (
          <PageWrapper isBlocker={false} Page={FlaggedConnectionsDashboard} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/turnOffAlarms",
        element: <PageWrapper isBlocker={false} Page={TurnOffAlarms} />
      },

      !isAnyKhattabaAdmin && {
        path: "/developer/flushHosts",
        element: <PageWrapper isBlocker={false} Page={FlushHosts} />
      },

      !isAnyKhattabaAdmin && {
        path: "/developer/WatchAlerts",
        element: <PageWrapper isBlocker={false} Page={WatchAlerts} />
      },
      !isAnyKhattabaAdmin && {
        path: "/khattabat/reassignKhattaba",
        element: <PageWrapper isBlocker={false} Page={ReassignKhataba} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/DebugImageAI",
        element: <PageWrapper isBlocker={false} Page={DebugImageAI} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/adminUsageData",
        element: <PageWrapper isBlocker={false} Page={AdminUsage} />
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/aiGeneratedDescriptionAll",
        element: (
          <PageWrapper isBlocker={false} Page={AIGeneratedDescriptionAll} />
        )
      },
      !isAnyKhattabaAdmin && {
        path: "/developer/transferUserPackage",
        element: <PageWrapper isBlocker={false} Page={TransferUserPackage} />
      },
      isAnyKhattabaAdmin && {
        path: "/khattabat/:gender",
        element: <PageWrapper isBlocker={false} Page={KhatabatClients} />
      },
      isAnyKhattabaAdmin && {
        path: "/khattabat/newClients",
        element: <PageWrapper isBlocker={false} Page={KhattabatNewClients} />
      },
      isAnyKhattabaAdmin && {
        path: "/khattabatMatching/searchAnd1O1M",
        element: (
          <PageWrapper isBlocker={false} Page={KhattabatSearchingAnd101M} />
        )
      },
      isAnyKhattabaAdmin && {
        path: "/khattabatMatching/results",
        element: <PageWrapper isBlocker={false} Page={KhattabatResults} />
      },
      !isAnyKhattabaAdmin && {
        path: "/shareholder/dashboard",
        element: <PageWrapper isBlocker={false} Page={ShareholdersDashboard} />
      },
      {
        path: "*",
        element: <Navigate to="/" replace />
      }
    ].filter(Boolean); // Filter out falsy values

    return createBrowserRouter(routes);
  }, [isAnyKhattabaAdmin]); // Recreate the router when the state changes

  return <RouterProvider router={router} />;
});

export default RouterComponent;
